import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, ExternalLink, Download } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Seo from "@/components/Seo";
import { Modal, Field, TextArea, SelectField, AdminButton } from "@/pages/admin/ui";
import ImageUploader from "@/components/admin/ImageUploader";
import GalleryEditor from "@/components/admin/GalleryEditor";
import RichTextEditor from "@/components/admin/RichTextEditor";
import TravelAdmin from "@/pages/admin/TravelAdmin";
import SupportersAdmin from "@/pages/admin/SupportersAdmin";
import LearningAdmin from "@/pages/admin/LearningAdmin";
import ExpeditionsAdmin from "@/pages/admin/ExpeditionsAdmin";
import LastHorizonAdmin from "@/pages/admin/LastHorizonAdmin";
import DawnAdmin from "@/pages/admin/DawnAdmin";
import { PROGRAMME_CATEGORIES, PROGRAMME_STATUSES, PROGRAMME_STATUS_LABEL, STORY_CATEGORIES, INDIA_STATES, WORLD_COUNTRIES, locationLabel, PARTNERSHIP_REQUIREMENT_TYPES } from "@/lib/content";
import { SCHOOLS, VOLUNTEER_CATEGORIES } from "@/lib/learning";

const REPORT_CATEGORIES = [
  "LEGAL & REGISTRATION", "GOVERNANCE", "ANNUAL REPORTS", "PROGRAMME REPORTS",
  "FINANCIAL INFORMATION", "POLICIES", "IMPACT REPORTS",
];
const REPORT_STATUSES = ["To be published", "In progress", "Available"];
const DIMENSIONS = ["PLANET", "PEOPLE", "KNOWLEDGE", "RESILIENCE"];

function CountrySelect({ value, onChange, allowNone = false }) {
  return (
    <label className="block">
      <span className="text-[0.65rem] uppercase tracking-[0.14em] text-ptf-text/60">Country</span>
      <select className="w-full border border-ptf-primary/20 px-3 py-2.5 text-sm outline-none focus:border-ptf-secondary transition-colors bg-white text-ptf-text mt-1" value={value} onChange={onChange} data-testid="country-select">
        {allowNone && <option value="">None</option>}
        {WORLD_COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
    </label>
  );
}

function RegionSelect({ value, onChange }) {
  return (
    <label className="block">
      <span className="text-[0.65rem] uppercase tracking-[0.14em] text-ptf-text/60">Region (State)</span>
      <select className="w-full border border-ptf-primary/20 px-3 py-2.5 text-sm outline-none focus:border-ptf-secondary transition-colors bg-white text-ptf-text mt-1" value={value} onChange={onChange} data-testid="region-select">
        <option value="">Select state</option>
        {INDIA_STATES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
      </select>
    </label>
  );
}

const TABS = ["Programmes", "Stories", "Experiences", "Films & Field", "Learning", "Last Horizon", "DAWN", "Reports", "Impact", "Submissions", "Supporters", "Settings"];

const toList = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);
const fromList = (a) => (a || []).join("\n");
const toKV = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean).map((line) => {
  const idx = line.indexOf(":");
  return idx === -1 ? { label: line, value: "", unit: "" } : { label: line.slice(0, idx).trim(), value: line.slice(idx + 1).trim(), unit: "" };
});
const fromKV = (a) => (a || []).map((k) => `${k.label}: ${k.value}`).join("\n");
const toNums = (s) => (s || "").split(",").map((x) => Number(x.trim())).filter((n) => Number.isFinite(n) && n > 0);
const fromNums = (a) => (a || []).join(", ");

function useEntity(key, path, listPath = path) {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: [key], queryFn: async () => (await api.get(`/${listPath}`)).data });
  const create = useMutation({
    mutationFn: (body) => api.post(`/admin/${path}`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: [key] }); toast.success("Created"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const update = useMutation({
    mutationFn: ({ id, body }) => api.put(`/admin/${path}/${id}`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: [key] }); toast.success("Updated"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const remove = useMutation({
    mutationFn: (id) => api.delete(`/admin/${path}/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: [key] }); toast.success("Deleted"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  return { list, create, update, remove };
}

function Row({ title, sub, onEdit, onDelete }) {
  return (
    <div className="flex items-center justify-between gap-4 border border-ptf-primary/10 bg-white px-5 py-4">
      <div className="min-w-0">
        <p className="font-medium text-ptf-primary truncate">{title}</p>
        {sub && <p className="text-xs text-ptf-text/55 truncate mt-0.5">{sub}</p>}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button onClick={onEdit} data-testid="row-edit" className="grid place-items-center h-9 w-9 border border-ptf-primary/15 hover:bg-ptf-primary/5"><Pencil className="h-4 w-4 text-ptf-secondary" /></button>
        <button onClick={onDelete} data-testid="row-delete" className="grid place-items-center h-9 w-9 border border-ptf-primary/15 hover:bg-red-50"><Trash2 className="h-4 w-4 text-red-600" /></button>
      </div>
    </div>
  );
}

function Section({ title, onAdd, children }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-display uppercase text-2xl tracking-tight text-ptf-primary">{title}</h2>
        {onAdd && <AdminButton onClick={onAdd} data-testid="add-new"><Plus className="h-4 w-4" /> Add</AdminButton>}
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

// ---------- Programmes ----------
function Programmes() {
  const { list, create, update, remove } = useEntity("programmes", "programmes");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = {
    name: "", category: PROGRAMME_CATEGORIES[0], location: "", country: "India", region: "", regions_s: "", district: "",
    scope: "INDIA", latitude: "", longitude: "", status: "CONCEPT", objective: "", challenge: "", approach: "",
    activities: "", partners: "", images: [], gallery: [], outcomes: "", impact: "", order: 0,
    short_description: "", start_date: "", end_date: "", duration: "", programme_type: "",
    target_beneficiaries: "", actual_beneficiaries: "", budget: "", currency: "USD", funding_requirement: "",
    sdgs: "", technology: "", community_role: "", training_component: "", volunteer_component: "",
    partnership_requirements: [], proposed_partners: "", target_kpis: "", actual_kpis: "", verified_impact: "",
    verification_status: "UNVERIFIED", featured: false, seo_title: "", seo_description: "",
    related_training_categories: [], related_volunteer_categories: [],
    related_journey_ids: "", related_film_slugs: "", related_story_ids: "", brief_document_url: "",
    donations_enabled: false, donation_title: "", donation_description: "",
    donation_suggested_amounts_s: "", donation_min_amount: 100, donation_thank_you_message: "",
  };
  const [f, setF] = useState(empty);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const toggleMulti = (k, v) => setF((p) => ({ ...p, [k]: p[k].includes(v) ? p[k].filter((x) => x !== v) : [...p[k], v] }));

  const openNew = () => { setEditing(null); setF(empty); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setF({
      ...empty, ...p,
      country: p.country || "India", region: p.region || "", regions_s: (p.regions || []).join(", "), district: p.district || "", scope: p.scope || "INDIA",
      latitude: p.latitude ?? "", longitude: p.longitude ?? "",
      images: p.images || [], gallery: p.gallery || [],
      activities: fromList(p.activities), partners: fromList(p.partners),
      sdgs: fromNums(p.sdgs), proposed_partners: fromList(p.proposed_partners),
      target_kpis: fromKV(p.target_kpis), actual_kpis: fromKV(p.actual_kpis), verified_impact: fromKV(p.verified_impact),
      partnership_requirements: p.partnership_requirements || [],
      related_training_categories: p.related_training_categories || [], related_volunteer_categories: p.related_volunteer_categories || [],
      related_journey_ids: fromList(p.related_journey_ids), related_film_slugs: fromList(p.related_film_slugs), related_story_ids: fromList(p.related_story_ids),
      donation_suggested_amounts_s: fromNums(p.donation_suggested_amounts),
    });
    setOpen(true);
  };
  const save = (e) => {
    e.preventDefault();
    const country = f.country || "India";
    const body = {
      ...f, country, region: country === "India" ? f.region : "",
      regions: (f.regions_s || "").split(",").map((s) => s.trim()).filter(Boolean),
      order: Number(f.order) || 0,
      activities: toList(f.activities), partners: toList(f.partners), images: f.images, gallery: f.gallery,
      latitude: f.latitude === "" ? null : Number(f.latitude), longitude: f.longitude === "" ? null : Number(f.longitude),
      sdgs: toNums(f.sdgs), proposed_partners: toList(f.proposed_partners),
      target_kpis: toKV(f.target_kpis), actual_kpis: toKV(f.actual_kpis), verified_impact: toKV(f.verified_impact),
      related_journey_ids: toList(f.related_journey_ids), related_film_slugs: toList(f.related_film_slugs), related_story_ids: toList(f.related_story_ids),
      donation_suggested_amounts: toNums(f.donation_suggested_amounts_s), donation_min_amount: Number(f.donation_min_amount) || 100,
    };
    if (editing) update.mutate({ id: editing.id, body }, { onSuccess: () => setOpen(false) });
    else create.mutate(body, { onSuccess: () => setOpen(false) });
  };

  return (
    <Section title="Programmes" onAdd={openNew}>
      {(list.data || []).length === 0 && <p className="text-sm text-ptf-text/50">No programmes yet. Add one to populate the Programmes page.</p>}
      {(list.data || []).map((p) => (
        <Row key={p.id} title={`${p.name}${p.featured ? " ★" : ""}`} sub={`${p.category} · ${p.status} · ${locationLabel(p) || p.country || "India"}`} onEdit={() => openEdit(p)} onDelete={() => { if (window.confirm("Delete this programme?")) remove.mutate(p.id); }} />
      ))}
      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Programme" : "New Programme"}>
        <form onSubmit={save} className="space-y-4">
          <Field label="Name *" required value={f.name} onChange={set("name")} />
          <TextArea label="Short Description (used on cards)" value={f.short_description} onChange={set("short_description")} />
          <div className="grid grid-cols-2 gap-4">
            <SelectField label="Programme Area" options={PROGRAMME_CATEGORIES} value={f.category} onChange={set("category")} />
            <SelectField label="Status" options={PROGRAMME_STATUSES} value={f.status} onChange={set("status")} />
          </div>

          <p className="text-[0.65rem] uppercase tracking-[0.16em] text-ptf-secondary pt-2">Location</p>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Location (free text)" value={f.location} onChange={set("location")} placeholder="e.g. Kaziranga" />
            <CountrySelect value={f.country || "India"} onChange={set("country")} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            {(f.country || "India") === "India" ? <RegionSelect value={f.region} onChange={set("region")} /> : <div />}
            <Field label="District" value={f.district} onChange={set("district")} />
          </div>
          <Field label="Additional map regions (comma-separated state names, for programmes spanning more than one state)" value={f.regions_s} onChange={set("regions_s")} placeholder="e.g. Gujarat, Maharashtra, Goa" />
          <div className="grid grid-cols-3 gap-4">
            <SelectField label="Scope" options={["INDIA", "WORLD"]} value={f.scope} onChange={set("scope")} />
            <Field label="Latitude" type="number" value={f.latitude} onChange={set("latitude")} placeholder="Leave empty if unknown" />
            <Field label="Longitude" type="number" value={f.longitude} onChange={set("longitude")} placeholder="Leave empty if unknown" />
          </div>

          <p className="text-[0.65rem] uppercase tracking-[0.16em] text-ptf-secondary pt-2">Timeline &amp; Type</p>
          <div className="grid grid-cols-3 gap-4">
            <Field label="Start Date" value={f.start_date} onChange={set("start_date")} placeholder="YYYY-MM-DD" />
            <Field label="Duration" value={f.duration} onChange={set("duration")} placeholder="e.g. 12 months" />
            <Field label="Programme Type" value={f.programme_type} onChange={set("programme_type")} placeholder="e.g. Proposed Pilot Programme" />
          </div>

          <p className="text-[0.65rem] uppercase tracking-[0.16em] text-ptf-secondary pt-2">Narrative</p>
          <TextArea label="The Challenge" value={f.challenge} onChange={set("challenge")} />
          <TextArea label="What We Are Trying To Do (Objective)" value={f.objective} onChange={set("objective")} />
          <TextArea label="Who It Serves (Target Beneficiaries)" value={f.target_beneficiaries} onChange={set("target_beneficiaries")} />
          <TextArea label="Programme Approach" value={f.approach} onChange={set("approach")} />
          <TextArea label="Technology / Methodology" value={f.technology} onChange={set("technology")} />
          <TextArea label="Community Participation" value={f.community_role} onChange={set("community_role")} />
          <TextArea label="Key Activities (one per line)" value={f.activities} onChange={set("activities")} />

          <p className="text-[0.65rem] uppercase tracking-[0.16em] text-ptf-secondary pt-2">Targets &amp; Verified Results (format: Label: Value, one per line)</p>
          <TextArea label="Target KPIs" value={f.target_kpis} onChange={set("target_kpis")} placeholder={"Species catalogued: 50+ species (target)"} />
          <TextArea label="Actual KPIs (only once measured)" value={f.actual_kpis} onChange={set("actual_kpis")} />
          <TextArea label="Verified Impact (only once independently verified)" value={f.verified_impact} onChange={set("verified_impact")} />
          <SelectField label="Verification Status" options={["UNVERIFIED", "VERIFIED"]} value={f.verification_status} onChange={set("verification_status")} />

          <p className="text-[0.65rem] uppercase tracking-[0.16em] text-ptf-secondary pt-2">Training &amp; Volunteering (Learn &amp; Train connections)</p>
          <TextArea label="Training Component (text)" value={f.training_component} onChange={set("training_component")} />
          <div>
            <span className="text-[0.65rem] uppercase tracking-[0.14em] text-ptf-text/60">Related Training Schools</span>
            <div className="flex flex-wrap gap-2 mt-2">
              {SCHOOLS.map((s) => (
                <button key={s.slug} type="button" onClick={() => toggleMulti("related_training_categories", s.slug)} className={`text-[0.62rem] uppercase tracking-[0.1em] px-2.5 py-1.5 border ${f.related_training_categories.includes(s.slug) ? "bg-ptf-primary text-ptf-bg border-ptf-primary" : "border-ptf-primary/20 text-ptf-text/60"}`}>{s.name}</button>
              ))}
            </div>
          </div>
          <TextArea label="Volunteer Component (text)" value={f.volunteer_component} onChange={set("volunteer_component")} />
          <div>
            <span className="text-[0.65rem] uppercase tracking-[0.14em] text-ptf-text/60">Related Volunteer Categories</span>
            <div className="flex flex-wrap gap-2 mt-2">
              {VOLUNTEER_CATEGORIES.map((c) => (
                <button key={c.name} type="button" onClick={() => toggleMulti("related_volunteer_categories", c.name)} className={`text-[0.62rem] uppercase tracking-[0.1em] px-2.5 py-1.5 border ${f.related_volunteer_categories.includes(c.name) ? "bg-ptf-primary text-ptf-bg border-ptf-primary" : "border-ptf-primary/20 text-ptf-text/60"}`}>{c.name}</button>
              ))}
            </div>
          </div>

          <p className="text-[0.65rem] uppercase tracking-[0.16em] text-ptf-secondary pt-2">Partnerships &amp; Funding</p>
          <div>
            <span className="text-[0.65rem] uppercase tracking-[0.14em] text-ptf-text/60">Partnership Requirements</span>
            <div className="flex flex-wrap gap-2 mt-2">
              {PARTNERSHIP_REQUIREMENT_TYPES.map((r) => (
                <button key={r} type="button" onClick={() => toggleMulti("partnership_requirements", r)} className={`text-[0.62rem] uppercase tracking-[0.1em] px-2.5 py-1.5 border ${f.partnership_requirements.includes(r) ? "bg-ptf-primary text-ptf-bg border-ptf-primary" : "border-ptf-primary/20 text-ptf-text/60"}`}>{r}</button>
              ))}
            </div>
          </div>
          <TextArea label="Funding Requirement / Ask" value={f.funding_requirement} onChange={set("funding_requirement")} />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Total Budget" value={f.budget} onChange={set("budget")} placeholder="e.g. 88,000" />
            <SelectField label="Currency" options={["USD", "INR", "EUR", "GBP"]} value={f.currency} onChange={set("currency")} />
          </div>
          <TextArea label="Partners (confirmed, one per line)" value={f.partners} onChange={set("partners")} />
          <TextArea label="Proposed Strategic Partners (not confirmed, one per line)" value={f.proposed_partners} onChange={set("proposed_partners")} />

          <p className="text-[0.65rem] uppercase tracking-[0.16em] text-ptf-secondary pt-2">SDGs, Related Content &amp; SEO</p>
          <Field label="SDGs (comma-separated numbers, e.g. 2, 4, 13)" value={f.sdgs} onChange={set("sdgs")} />
          <TextArea label="Related Expedition IDs (one per line)" value={f.related_journey_ids} onChange={set("related_journey_ids")} />
          <TextArea label="Related Film Slugs (one per line)" value={f.related_film_slugs} onChange={set("related_film_slugs")} />
          <TextArea label="Related Story IDs (one per line)" value={f.related_story_ids} onChange={set("related_story_ids")} />
          <Field label="SEO Title" value={f.seo_title} onChange={set("seo_title")} />
          <TextArea label="SEO Description" value={f.seo_description} onChange={set("seo_description")} />

          <p className="text-[0.65rem] uppercase tracking-[0.16em] text-ptf-secondary pt-2">Media &amp; Display</p>
          <ImageUploader multiple label="Cover images (first is the hero)" value={f.images} onChange={(v) => setF((p) => ({ ...p, images: v }))} />
          <GalleryEditor value={f.gallery} onChange={(v) => setF((p) => ({ ...p, gallery: v }))} />
          <TextArea label="Outcomes (only once achieved)" value={f.outcomes} onChange={set("outcomes")} />
          <TextArea label="Impact (only once verified)" value={f.impact} onChange={set("impact")} />
          <Field label="Order" type="number" value={f.order} onChange={set("order")} />
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.featured} onChange={(e) => setF((p) => ({ ...p, featured: e.target.checked }))} /> Featured on Homepage</label>

          <p className="text-[0.65rem] uppercase tracking-[0.16em] text-ptf-secondary pt-2">Your Contribution (Donations)</p>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="donations-enabled-toggle" checked={f.donations_enabled} onChange={(e) => setF((p) => ({ ...p, donations_enabled: e.target.checked }))} /> Accept contributions for this programme</label>
          {f.donations_enabled && (
            <>
              <Field label="Donation Title (shown on card, defaults to programme name)" value={f.donation_title} onChange={set("donation_title")} />
              <TextArea label="Donation Description" value={f.donation_description} onChange={set("donation_description")} />
              <div className="grid grid-cols-2 gap-4">
                <Field label="Suggested Amounts (comma-separated, e.g. 500, 1000, 2500)" value={f.donation_suggested_amounts_s} onChange={set("donation_suggested_amounts_s")} />
                <Field label="Minimum Amount (₹)" type="number" value={f.donation_min_amount} onChange={set("donation_min_amount")} />
              </div>
              <TextArea label="Thank You Message (shown after successful payment)" value={f.donation_thank_you_message} onChange={set("donation_thank_you_message")} />
            </>
          )}

          <div className="flex justify-end gap-3 pt-2"><AdminButton variant="ghost" type="button" onClick={() => setOpen(false)}>Cancel</AdminButton><AdminButton type="submit" data-testid="save-programme">Save</AdminButton></div>
        </form>
      </Modal>
    </Section>
  );
}

// ---------- Species (per-programme) ----------
function SpeciesAdmin() {
  const { list: programmesList } = useEntity("programmes", "programmes");
  const { list, create, update, remove } = useEntity("species", "species");
  const [programmeId, setProgrammeId] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = {
    common_name: "", scientific_name: "", conservation_status: "", legal_protection_status: "",
    habitat: "", threats: "", location: "", programme_role: "", monitoring_method: "",
    rescue_relevance: "", awareness_material: "", images: [], field_records: "",
    related_training_categories: [], related_volunteer_categories: [], related_story_ids: "", related_film_slugs: "", order: 0,
  };
  const [f, setF] = useState(empty);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const toggleMulti = (k, v) => setF((p) => ({ ...p, [k]: p[k].includes(v) ? p[k].filter((x) => x !== v) : [...p[k], v] }));

  const programmes = programmesList.data || [];
  const species = (list.data || []).filter((s) => s.programme_id === programmeId);

  const openNew = () => { setEditing(null); setF(empty); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setF({ ...empty, ...s, images: s.images || [], field_records: fromList(s.field_records), related_story_ids: fromList(s.related_story_ids), related_film_slugs: fromList(s.related_film_slugs), related_training_categories: s.related_training_categories || [], related_volunteer_categories: s.related_volunteer_categories || [] });
    setOpen(true);
  };
  const save = (e) => {
    e.preventDefault();
    const body = {
      ...f, programme_id: editing ? editing.programme_id : programmeId, order: Number(f.order) || 0,
      field_records: toList(f.field_records), related_story_ids: toList(f.related_story_ids), related_film_slugs: toList(f.related_film_slugs),
    };
    if (editing) update.mutate({ id: editing.id, body }, { onSuccess: () => setOpen(false) });
    else create.mutate(body, { onSuccess: () => setOpen(false) });
  };

  return (
    <div className="mt-14">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-display uppercase text-2xl tracking-tight text-ptf-primary">Species</h2>
        {programmeId && <AdminButton onClick={openNew} data-testid="add-species"><Plus className="h-4 w-4" /> Add Species</AdminButton>}
      </div>
      <label className="block mb-5 max-w-md">
        <span className="text-[0.65rem] uppercase tracking-[0.14em] text-ptf-text/60">Select Programme to Manage Species</span>
        <select data-testid="species-programme-select" className="w-full border border-ptf-primary/20 px-3 py-2.5 text-sm outline-none focus:border-ptf-secondary transition-colors bg-white text-ptf-text mt-1" value={programmeId} onChange={(e) => setProgrammeId(e.target.value)}>
          <option value="">Select a programme</option>
          {programmes.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </label>
      {programmeId && species.length === 0 && <p className="text-sm text-ptf-text/50">No species added yet for this programme.</p>}
      <div className="space-y-2">
        {species.map((s) => (
          <Row key={s.id} title={s.common_name} sub={s.scientific_name || s.conservation_status} onEdit={() => openEdit(s)} onDelete={() => { if (window.confirm("Delete this species record?")) remove.mutate(s.id); }} />
        ))}
      </div>
      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Species" : "New Species"}>
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Common Name *" required value={f.common_name} onChange={set("common_name")} />
            <Field label="Scientific Name" value={f.scientific_name} onChange={set("scientific_name")} />
          </div>
          <TextArea label="Conservation Status" value={f.conservation_status} onChange={set("conservation_status")} placeholder="e.g. IUCN Red List: Least Concern, or 'To be determined through field assessment'" />
          <TextArea label="Legal / Protection Status (only if verified)" value={f.legal_protection_status} onChange={set("legal_protection_status")} />
          <TextArea label="Habitat" value={f.habitat} onChange={set("habitat")} />
          <TextArea label="Threats" value={f.threats} onChange={set("threats")} />
          <Field label="Location (general area, avoid precise sensitive coordinates)" value={f.location} onChange={set("location")} />
          <TextArea label="Programme Role" value={f.programme_role} onChange={set("programme_role")} />
          <TextArea label="Monitoring Method" value={f.monitoring_method} onChange={set("monitoring_method")} />
          <TextArea label="Rescue Relevance" value={f.rescue_relevance} onChange={set("rescue_relevance")} />
          <TextArea label="Awareness Material" value={f.awareness_material} onChange={set("awareness_material")} />
          <TextArea label="Field Records (one per line)" value={f.field_records} onChange={set("field_records")} />
          <div>
            <span className="text-[0.65rem] uppercase tracking-[0.14em] text-ptf-text/60">Related Training Schools</span>
            <div className="flex flex-wrap gap-2 mt-2">
              {SCHOOLS.map((sc) => (
                <button key={sc.slug} type="button" onClick={() => toggleMulti("related_training_categories", sc.slug)} className={`text-[0.62rem] uppercase tracking-[0.1em] px-2.5 py-1.5 border ${f.related_training_categories.includes(sc.slug) ? "bg-ptf-primary text-ptf-bg border-ptf-primary" : "border-ptf-primary/20 text-ptf-text/60"}`}>{sc.name}</button>
              ))}
            </div>
          </div>
          <div>
            <span className="text-[0.65rem] uppercase tracking-[0.14em] text-ptf-text/60">Related Volunteer Categories</span>
            <div className="flex flex-wrap gap-2 mt-2">
              {VOLUNTEER_CATEGORIES.map((c) => (
                <button key={c.name} type="button" onClick={() => toggleMulti("related_volunteer_categories", c.name)} className={`text-[0.62rem] uppercase tracking-[0.1em] px-2.5 py-1.5 border ${f.related_volunteer_categories.includes(c.name) ? "bg-ptf-primary text-ptf-bg border-ptf-primary" : "border-ptf-primary/20 text-ptf-text/60"}`}>{c.name}</button>
              ))}
            </div>
          </div>
          <TextArea label="Related Story IDs (one per line)" value={f.related_story_ids} onChange={set("related_story_ids")} />
          <TextArea label="Related Film Slugs (one per line)" value={f.related_film_slugs} onChange={set("related_film_slugs")} />
          <ImageUploader multiple label="Species images" value={f.images} onChange={(v) => setF((p) => ({ ...p, images: v }))} />
          <Field label="Order" type="number" value={f.order} onChange={set("order")} />
          <div className="flex justify-end gap-3 pt-2"><AdminButton variant="ghost" type="button" onClick={() => setOpen(false)}>Cancel</AdminButton><AdminButton type="submit" data-testid="save-species">Save</AdminButton></div>
        </form>
      </Modal>
    </div>
  );
}

// ---------- Stories ----------
function Stories() {
  const { list, create, update, remove } = useEntity("stories-admin", "stories", "admin/stories");
  return <StoriesInner list={list} create={create} update={update} remove={remove} />;
}
function StoriesInner({ list, create, update, remove }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = { title: "", category: STORY_CATEGORIES[0], country: "", region: "", date: "", author: "", excerpt: "", content: "", image: "", published: false };
  const [f, setF] = useState(empty);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const openNew = () => { setEditing(null); setF(empty); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setF({ ...s, country: s.country || "", region: s.region || "" }); setOpen(true); };
  const save = (e) => {
    e.preventDefault();
    const body = { ...f, region: (f.country || "") === "India" ? f.region : "" };
    if (editing) update.mutate({ id: editing.id, body }, { onSuccess: () => setOpen(false) });
    else create.mutate(body, { onSuccess: () => setOpen(false) });
  };
  return (
    <Section title="Stories" onAdd={openNew}>
      {(list.data || []).length === 0 && <p className="text-sm text-ptf-text/50">No stories yet.</p>}
      {(list.data || []).map((s) => (
        <Row key={s.id} title={`${s.title}${s.published ? "" : " (draft)"}`} sub={`${s.category}${s.date ? " · " + s.date : ""}${locationLabel(s) ? " · " + locationLabel(s) : s.country ? " · " + s.country : ""}`} onEdit={() => openEdit(s)} onDelete={() => { if (window.confirm("Delete this story?")) remove.mutate(s.id); }} />
      ))}
      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Story" : "New Story"}>
        <form onSubmit={save} className="space-y-4">
          <Field label="Title *" required value={f.title} onChange={set("title")} />
          <div className="grid grid-cols-2 gap-4">
            <SelectField label="Category" options={STORY_CATEGORIES} value={f.category} onChange={set("category")} />
            <Field label="Date (e.g. Jun 2026)" value={f.date} onChange={set("date")} />
          </div>
          <Field label="Author" value={f.author} onChange={set("author")} />
          <div className="grid grid-cols-2 gap-4">
            <CountrySelect allowNone value={f.country || ""} onChange={set("country")} />
            {(f.country || "") === "India" ? <RegionSelect value={f.region} onChange={set("region")} /> : <div />}
          </div>
          <ImageUploader label="Cover Image (upload)" value={f.image} onChange={(v) => setF((p) => ({ ...p, image: v }))} />
          <TextArea label="Excerpt" value={f.excerpt} onChange={set("excerpt")} />
          <RichTextEditor label="Content" value={f.content} onChange={(v) => setF((p) => ({ ...p, content: v }))} />
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.published} onChange={(e) => setF((p) => ({ ...p, published: e.target.checked }))} /> Published</label>
          <div className="flex justify-end gap-3 pt-2"><AdminButton variant="ghost" type="button" onClick={() => setOpen(false)}>Cancel</AdminButton><AdminButton type="submit" data-testid="save-story">Save</AdminButton></div>
        </form>
      </Modal>
    </Section>
  );
}

// ---------- Reports ----------
function Reports() {
  const { list, create, update, remove } = useEntity("reports", "reports");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = { title: "", category: REPORT_CATEGORIES[0], status: "To be published", url: "", order: 0 };
  const [f, setF] = useState(empty);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const openNew = () => { setEditing(null); setF(empty); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setF({ ...r }); setOpen(true); };
  const save = (e) => {
    e.preventDefault();
    const body = { ...f, order: Number(f.order) || 0 };
    if (editing) update.mutate({ id: editing.id, body }, { onSuccess: () => setOpen(false) });
    else create.mutate(body, { onSuccess: () => setOpen(false) });
  };
  return (
    <Section title="Transparency Documents" onAdd={openNew}>
      {(list.data || []).map((r) => (
        <Row key={r.id} title={r.title} sub={`${r.category} · ${r.status}`} onEdit={() => openEdit(r)} onDelete={() => { if (window.confirm("Delete this document?")) remove.mutate(r.id); }} />
      ))}
      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Document" : "New Document"}>
        <form onSubmit={save} className="space-y-4">
          <Field label="Title *" required value={f.title} onChange={set("title")} />
          <SelectField label="Category" options={REPORT_CATEGORIES} value={f.category} onChange={set("category")} />
          <div className="grid grid-cols-2 gap-4">
            <SelectField label="Status" options={REPORT_STATUSES} value={f.status} onChange={set("status")} />
            <Field label="Order" type="number" value={f.order} onChange={set("order")} />
          </div>
          <Field label="Document URL (when available)" value={f.url} onChange={set("url")} placeholder="https://…" />
          <div className="flex justify-end gap-3 pt-2"><AdminButton variant="ghost" type="button" onClick={() => setOpen(false)}>Cancel</AdminButton><AdminButton type="submit" data-testid="save-report">Save</AdminButton></div>
        </form>
      </Modal>
    </Section>
  );
}

// ---------- Impact ----------
function Impact() {
  const { list, create, update, remove } = useEntity("impact", "impact");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = { dimension: "PLANET", label: "", value: "", unit: "", category: "", location: "", programme: "", source: "", status: "DOCUMENTED", last_verified: "", date: "", note: "", order: 0 };
  const [f, setF] = useState(empty);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const openNew = () => { setEditing(null); setF(empty); setOpen(true); };
  const openEdit = (m) => { setEditing(m); setF({ ...m }); setOpen(true); };
  const save = (e) => {
    e.preventDefault();
    const body = { ...f, order: Number(f.order) || 0 };
    if (editing) update.mutate({ id: editing.id, body }, { onSuccess: () => setOpen(false) });
    else create.mutate(body, { onSuccess: () => setOpen(false) });
  };
  return (
    <Section title="Impact Figures" onAdd={openNew}>
      <p className="text-sm text-ptf-text/55 -mt-3 mb-3">Only add verified figures. These appear on the Impact page under their dimension.</p>
      {(list.data || []).length === 0 && <p className="text-sm text-ptf-text/50">No figures yet. The Impact page shows the framework only.</p>}
      {(list.data || []).map((m) => (
        <Row key={m.id} title={`${m.value} · ${m.label}`} sub={m.dimension} onEdit={() => openEdit(m)} onDelete={() => { if (window.confirm("Delete this figure?")) remove.mutate(m.id); }} />
      ))}
      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Figure" : "New Figure"}>
        <form onSubmit={save} className="space-y-4">
          <SelectField label="Dimension" options={DIMENSIONS} value={f.dimension} onChange={set("dimension")} />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Value *" required value={f.value} onChange={set("value")} placeholder="e.g. 450" data-testid="impact-value" />
            <Field label="Unit" value={f.unit || ""} onChange={set("unit")} placeholder="e.g. trees" data-testid="impact-unit" />
          </div>
          <Field label="Label *" required value={f.label} onChange={set("label")} placeholder="e.g. Trees planted in one field day" data-testid="impact-label" />
          <div className="grid grid-cols-2 gap-4">
            <SelectField label="Category" options={["", "Communities", "Education", "Health & Hygiene", "Clean Energy", "Water", "Environment", "Rural Tourism", "Adventure & Sports", "Wildlife", "Livelihoods"]} value={f.category || ""} onChange={set("category")} data-testid="impact-category" />
            <SelectField label="Status" options={["DOCUMENTED", "HISTORICAL", "PILOT", "PLANNED", "VERIFY"]} value={f.status || "DOCUMENTED"} onChange={set("status")} data-testid="impact-status" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Location" value={f.location || ""} onChange={set("location")} placeholder="e.g. Kuna, Tiuni, Uttarakhand" data-testid="impact-location" />
            <Field label="Programme" value={f.programme || ""} onChange={set("programme")} placeholder="e.g. Project Dawn" data-testid="impact-programme" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Source" value={f.source || ""} onChange={set("source")} placeholder="e.g. Field report, Feb 2024" data-testid="impact-source" />
            <Field label="Last verified" value={f.last_verified || ""} onChange={set("last_verified")} placeholder="e.g. Jun 2026" data-testid="impact-last-verified" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Milestone date" value={f.date} onChange={set("date")} placeholder="e.g. Jun 2026" data-testid="impact-date" />
            <Field label="Order" type="number" value={f.order} onChange={set("order")} />
          </div>
          <TextArea label="Note (shown on the timeline)" value={f.note} onChange={set("note")} data-testid="impact-note" />
          <div className="flex justify-end gap-3 pt-2"><AdminButton variant="ghost" type="button" onClick={() => setOpen(false)}>Cancel</AdminButton><AdminButton type="submit" data-testid="save-impact">Save</AdminButton></div>
        </form>
      </Modal>
    </Section>
  );
}

// ---------- Submissions ----------
function Submissions() {
  const qc = useQueryClient();
  const partners = useQuery({ queryKey: ["pe"], queryFn: async () => (await api.get("/admin/partnership-enquiries")).data });
  const contacts = useQuery({ queryKey: ["cs"], queryFn: async () => (await api.get("/admin/contact-submissions")).data });
  const delPartner = useMutation({
    mutationFn: (id) => api.delete(`/admin/partnership-enquiries/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["pe"] }); toast.success("Enquiry deleted"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const delContact = useMutation({
    mutationFn: (id) => api.delete(`/admin/contact-submissions/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["cs"] }); toast.success("Message deleted"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const subscribers = useQuery({ queryKey: ["nl"], queryFn: async () => (await api.get("/admin/newsletter")).data });
  const delSubscriber = useMutation({
    mutationFn: (id) => api.delete(`/admin/newsletter/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["nl"] }); toast.success("Subscriber removed"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const fmt = (d) => new Date(d).toLocaleString("en-IN");
  const downloadCsv = () => {
    const rows = [["email", "subscribed_at"], ...(subscribers.data || []).map((s) => [s.email, s.created_at])];
    const csv = rows.map((r) => r.map((v) => `"${String(v).replaceAll('"', '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "newsletter-subscribers.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };
  return (
    <div className="space-y-12">
      <div>
        <h2 className="font-display uppercase text-2xl tracking-tight text-ptf-primary mb-5">Partnership Enquiries ({partners.data?.length || 0})</h2>
        <div className="space-y-2">
          {(partners.data || []).length === 0 && <p className="text-sm text-ptf-text/50">No enquiries yet.</p>}
          {(partners.data || []).map((p) => (
            <div key={p.id} className="border border-ptf-primary/10 bg-white p-5">
              <div className="flex flex-wrap justify-between gap-2">
                <p className="font-medium text-ptf-primary">{p.name}{p.organisation ? ` · ${p.organisation}` : ""}</p>
                <span className="flex items-center gap-3">
                  <span className="text-xs text-ptf-text/45">{fmt(p.created_at)}</span>
                  <button onClick={() => { if (window.confirm("Delete this enquiry?")) delPartner.mutate(p.id); }} data-testid="delete-enquiry" className="grid place-items-center h-7 w-7 border border-ptf-primary/15 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5 text-red-600" /></button>
                </span>
              </div>
              <p className="text-sm text-ptf-secondary mt-1">{p.email}{p.phone ? ` · ${p.phone}` : ""}</p>
              <p className="text-xs uppercase tracking-[0.12em] text-ptf-text/55 mt-1">{p.organisation_type} {p.area_of_interest ? `· ${p.area_of_interest}` : ""}</p>
              <p className="text-sm text-ptf-text/75 mt-3">{p.message}</p>
            </div>
          ))}
        </div>
      </div>
      <div>
        <h2 className="font-display uppercase text-2xl tracking-tight text-ptf-primary mb-5">Contact Messages ({contacts.data?.length || 0})</h2>
        <div className="space-y-2">
          {(contacts.data || []).length === 0 && <p className="text-sm text-ptf-text/50">No messages yet.</p>}
          {(contacts.data || []).map((c) => (
            <div key={c.id} className="border border-ptf-primary/10 bg-white p-5">
              <div className="flex flex-wrap justify-between gap-2">
                <p className="font-medium text-ptf-primary">{c.name}</p>
                <span className="flex items-center gap-3">
                  <span className="text-xs text-ptf-text/45">{fmt(c.created_at)}</span>
                  <button onClick={() => { if (window.confirm("Delete this message?")) delContact.mutate(c.id); }} data-testid="delete-contact" className="grid place-items-center h-7 w-7 border border-ptf-primary/15 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5 text-red-600" /></button>
                </span>
              </div>
              <p className="text-sm text-ptf-secondary mt-1">{c.email}</p>
              <p className="text-xs uppercase tracking-[0.12em] text-ptf-text/55 mt-1">{c.category} {c.subject ? `· ${c.subject}` : ""}</p>
              <p className="text-sm text-ptf-text/75 mt-3">{c.message}</p>
            </div>
          ))}
        </div>
      </div>
      <div>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <h2 className="font-display uppercase text-2xl tracking-tight text-ptf-primary">Newsletter Subscribers ({subscribers.data?.length || 0})</h2>
          {(subscribers.data || []).length > 0 && (
            <button onClick={downloadCsv} data-testid="export-subscribers-csv" className="inline-flex items-center gap-2 border border-ptf-primary/20 px-4 py-2 text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-ptf-primary hover:bg-ptf-primary/5 transition-colors">
              <Download className="h-3.5 w-3.5" /> Download CSV
            </button>
          )}
        </div>
        <div className="space-y-2">
          {(subscribers.data || []).length === 0 && <p className="text-sm text-ptf-text/50">No subscribers yet.</p>}
          {(subscribers.data || []).map((s) => (
            <div key={s.id} data-testid="newsletter-subscriber-row" className="border border-ptf-primary/10 bg-white px-5 py-3 flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-medium text-ptf-primary">{s.email}</p>
              <span className="flex items-center gap-3">
                <span className="text-xs text-ptf-text/45">{fmt(s.created_at)}</span>
                <button onClick={() => { if (window.confirm("Remove this subscriber?")) delSubscriber.mutate(s.id); }} data-testid="delete-subscriber" className="grid place-items-center h-7 w-7 border border-ptf-primary/15 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5 text-red-600" /></button>
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------- Gmail Connection ----------
function GmailConnection() {
  const { data, refetch } = useQuery({ queryKey: ["gmail-status"], queryFn: async () => (await api.get("/admin/gmail/status")).data });
  const connect = useMutation({
    mutationFn: async () => (await api.get("/admin/gmail/connect")).data,
    onSuccess: (d) => { window.location.href = d.auth_url; },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const disconnect = useMutation({
    mutationFn: () => api.post("/admin/gmail/disconnect"),
    onSuccess: () => { toast.success("Gmail disconnected"); refetch(); },
  });
  return (
    <div className="border border-ptf-primary/15 p-5 space-y-3" data-testid="gmail-connection-card">
      <p className="text-[0.65rem] uppercase tracking-[0.18em] text-ptf-primary/60">System Email (Gmail)</p>
      {data?.connected ? (
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-ptf-primary" data-testid="gmail-connected-status">
            Connected as <span className="font-semibold">{data.email}</span>. All system emails now send from this Gmail account.
          </p>
          <button type="button" onClick={() => { if (window.confirm("Disconnect Gmail and fall back to default email sender?")) disconnect.mutate(); }} data-testid="gmail-disconnect-btn" className="text-xs uppercase tracking-[0.14em] text-red-600 hover:text-red-700 shrink-0">Disconnect</button>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-ptf-text/70">Not connected. System emails currently send via the default managed sender.</p>
          <AdminButton type="button" onClick={() => connect.mutate()} data-testid="gmail-connect-btn">Connect Gmail</AdminButton>
        </div>
      )}
    </div>
  );
}

// ---------- Google Drive Connection (Last Horizon media backup) ----------
function DriveConnection() {
  const { data, refetch } = useQuery({ queryKey: ["drive-status"], queryFn: async () => (await api.get("/admin/drive/status")).data });
  const connect = useMutation({
    mutationFn: async () => (await api.get("/admin/drive/connect")).data,
    onSuccess: (d) => { window.location.href = d.auth_url; },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const disconnect = useMutation({
    mutationFn: () => api.post("/admin/drive/disconnect"),
    onSuccess: () => { toast.success("Google Drive disconnected"); refetch(); },
  });
  return (
    <div className="border border-ptf-primary/15 p-5 space-y-3" data-testid="drive-connection-card">
      <p className="text-[0.65rem] uppercase tracking-[0.18em] text-ptf-primary/60">Google Drive (Last Horizon Media Backup)</p>
      {data?.connected ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-ptf-primary" data-testid="drive-connected-status">
            Connected as <span className="font-semibold">{data.email}</span>. Upload-mode applicant photos/video now auto-back-up to Drive on submission.
            {data.folder_url && <> <a href={data.folder_url} target="_blank" rel="noopener noreferrer" className="underline">Open Drive folder</a></>}
          </p>
          <button type="button" onClick={() => { if (window.confirm("Disconnect Google Drive? Future applicant submissions will not be backed up to Drive.")) disconnect.mutate(); }} data-testid="drive-disconnect-btn" className="text-xs uppercase tracking-[0.14em] text-red-600 hover:text-red-700 shrink-0">Disconnect</button>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-ptf-text/70">Not connected. Applicants using "Upload Files" mode are stored only in Foundation storage (participant-provided Drive links still work either way).</p>
          <AdminButton type="button" onClick={() => connect.mutate()} data-testid="drive-connect-btn">Connect Google Drive</AdminButton>
        </div>
      )}
    </div>
  );
}

// ---------- Settings ----------
function Settings() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const [f, setF] = useState(null);
  const current = f || data || {};
  const set = (k) => (e) => setF({ ...current, [k]: e.target.value });
  const mut = useMutation({
    mutationFn: (body) => api.put("/admin/settings", body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["settings"] }); toast.success("Settings saved"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const save = (e) => { e.preventDefault(); mut.mutate(current); };
  return (
    <div className="max-w-xl">
      <h2 className="font-display uppercase text-2xl tracking-tight text-ptf-primary mb-5">Site Settings</h2>
      <form onSubmit={save} className="space-y-4">
        <Field label="Contact Email" value={current.contact_email || ""} onChange={set("contact_email")} />
        <Field label="Phone" value={current.phone || ""} onChange={set("phone")} data-testid="settings-phone" />
        <Field label="Address" value={current.address || ""} onChange={set("address")} data-testid="settings-address" />
        <div className="grid grid-cols-2 gap-4">
          <Field label="Twitter URL" value={current.twitter || ""} onChange={set("twitter")} />
          <Field label="Instagram URL" value={current.instagram || ""} onChange={set("instagram")} />
          <Field label="LinkedIn URL" value={current.linkedin || ""} onChange={set("linkedin")} />
          <Field label="YouTube URL" value={current.youtube || ""} onChange={set("youtube")} />
        </div>
        <div className="border border-ptf-primary/15 p-5 space-y-3">
          <p className="text-[0.65rem] uppercase tracking-[0.18em] text-ptf-primary/60">Expedition terrain card images (URL or /path; blank = default)</p>
          {["MOUNTAINS", "RIVERS", "OCEANS", "LAND", "WILDLIFE"].map((k) => (
            <Field key={k} label={k} value={current.terrain_images?.[k] || ""} onChange={(e) => setF({ ...current, terrain_images: { ...(current.terrain_images || {}), [k]: e.target.value } })} data-testid={`settings-terrain-${k.toLowerCase()}`} />
          ))}
        </div>
        <div className="border border-ptf-primary/15 p-5 space-y-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={!!current.announcement_enabled}
              onChange={(e) => setF({ ...current, announcement_enabled: e.target.checked })}
              data-testid="announcement-toggle"
              className="h-4 w-4 accent-ptf-secondary"
            />
            <span className="text-sm font-medium text-ptf-primary">Show site-wide announcement bar</span>
          </label>
          <Field label="Announcement text" value={current.announcement_text || ""} onChange={set("announcement_text")} placeholder="e.g. Our first programme is live" data-testid="announcement-text" />
          <Field label="Announcement link (optional, e.g. /programmes)" value={current.announcement_link || ""} onChange={set("announcement_link")} placeholder="/programmes" data-testid="announcement-link" />
        </div>
        <AdminButton type="submit" data-testid="save-settings">Save Settings</AdminButton>
      </form>
      <div className="mt-6 space-y-4">
        <GmailConnection />
        <DriveConnection />
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [tab, setTab] = useState("Programmes");
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Intentionally run once on mount to consume one-shot URL params (Gmail/Drive OAuth
  // callbacks, ?tab=) - adding searchParams/setSearchParams would re-trigger this effect
  // every time setSearchParams below runs, causing an infinite loop.
  useEffect(() => {
    const wantedTab = searchParams.get("tab");
    if (wantedTab && TABS.includes(wantedTab)) setTab(wantedTab);
    if (searchParams.get("gmail_connected")) toast.success("Gmail connected successfully");
    if (searchParams.get("gmail_error")) toast.error(`Gmail connection failed: ${searchParams.get("gmail_error")}`);
    if (searchParams.get("drive_connected")) toast.success("Google Drive connected successfully");
    if (searchParams.get("drive_error")) toast.error(`Google Drive connection failed: ${searchParams.get("drive_error")}`);
    if (searchParams.get("gmail_connected") || searchParams.get("gmail_error") || searchParams.get("drive_connected") || searchParams.get("drive_error") || wantedTab) setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Seo title="Dashboard" path="/admin" />
      <div className="min-h-screen bg-ptf-bg">
        <header className="bg-ptf-primary text-ptf-bg">
          <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="grid place-items-center h-9 w-9 rounded-full border border-ptf-bg/40"><span className="h-2.5 w-2.5 rounded-full bg-ptf-accent" /></span>
              <span className="font-display uppercase text-lg tracking-tight">PTF Admin</span>
            </div>
            <div className="flex items-center gap-4">
              <a href="/" target="_blank" rel="noreferrer" className="text-xs uppercase tracking-[0.14em] text-ptf-bg/70 hover:text-ptf-accent flex items-center gap-1.5"><ExternalLink className="h-4 w-4" /> View site</a>
              <button onClick={async () => { await logout(); navigate("/admin/login"); }} data-testid="logout-btn" className="text-xs uppercase tracking-[0.14em] text-ptf-bg/70 hover:text-ptf-accent flex items-center gap-1.5"><LogOut className="h-4 w-4" /> Logout</button>
            </div>
          </div>
        </header>

        <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-8">
          <div className="flex flex-wrap gap-2 mb-10 border-b border-ptf-primary/15 pb-4">
            {TABS.map((t) => (
              <button key={t} data-testid={`tab-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`} onClick={() => setTab(t)} className={`px-4 py-2 text-[0.7rem] font-semibold uppercase tracking-[0.14em] transition-colors ${tab === t ? "bg-ptf-primary text-ptf-bg" : "text-ptf-text/60 hover:text-ptf-primary"}`}>{t}</button>
            ))}
          </div>

          {tab === "Programmes" && <><Programmes /><SpeciesAdmin /></>}
          {tab === "Stories" && <Stories />}
          {tab === "Experiences" && <TravelAdmin />}
          {tab === "Films & Field" && <ExpeditionsAdmin />}
          {tab === "Learning" && <LearningAdmin />}
          {tab === "Last Horizon" && <LastHorizonAdmin />}
          {tab === "DAWN" && <DawnAdmin />}
          {tab === "Reports" && <Reports />}
          {tab === "Impact" && <Impact />}
          {tab === "Submissions" && <Submissions />}
          {tab === "Supporters" && <SupportersAdmin />}
          {tab === "Settings" && <Settings />}
        </div>
      </div>
    </>
  );
}
